import React from 'react';
import { Box, IconButton, Tooltip, Chip } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

interface UndoRedoButtonsProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  undoDescription?: string;
  redoDescription?: string;
  size?: 'small' | 'medium' | 'large';
}

export const UndoRedoButtons: React.FC<UndoRedoButtonsProps> = ({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  undoDescription = 'Undo cell edit',
  redoDescription = 'Redo cell edit',
  size = 'medium',
}) => {
  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Z (or Cmd+Z on Mac) for undo
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 'z' &&
        !event.shiftKey
      ) {
        event.preventDefault();
        if (canUndo) {
          onUndo();
        }
      }

      // Ctrl+Y (or Cmd+Shift+Z on Mac) for redo
      if (
        ((event.ctrlKey || event.metaKey) && event.key === 'y') ||
        ((event.ctrlKey || event.metaKey) &&
          event.shiftKey &&
          event.key === 'z')
      ) {
        event.preventDefault();
        if (canRedo) {
          onRedo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canUndo, canRedo, onUndo, onRedo]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        label="Edits"
        size="small"
        variant="text"
        sx={{
          fontSize: '0.75rem',
          height: 24,
          color: 'text.secondary',
          borderColor: 'divider',
        }}
      />

      <Tooltip title={canUndo ? undoDescription : 'No actions to undo'}>
        <span>
          <IconButton
            onClick={onUndo}
            disabled={!canUndo}
            size={size}
            aria-label="Undo cell changes"
            sx={{
              color: canUndo ? 'primary.main' : 'action.disabled',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
              },
            }}
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={canRedo ? redoDescription : 'No actions to redo'}>
        <span>
          <IconButton
            onClick={onRedo}
            disabled={!canRedo}
            size={size}
            aria-label="Redo cell changes"
            sx={{
              color: canRedo ? 'primary.main' : 'action.disabled',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
              },
            }}
          >
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default UndoRedoButtons;
